import React, { useState } from 'react';
import {
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { changePassword } from '../services/api';

type PasswordField = 'current' | 'next' | 'confirm';

const inputClass =
  'w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3.5 pr-12 text-sm font-medium text-gray-900 outline-none transition focus:border-yellow-300 focus:bg-white focus:ring-4 focus:ring-yellow-100';

const AccountSecurityCard: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visible, setVisible] = useState<Record<PasswordField, boolean>>({
    current: false,
    next: false,
    confirm: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const toggleVisibility = (field: PasswordField) => {
    setVisible((previous) => ({
      ...previous,
      [field]: !previous[field],
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setIsError(true);
      setMessage('请填写全部密码字段');
      return;
    }
    if (newPassword.length < 8) {
      setIsError(true);
      setMessage('新密码至少需要 8 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage('两次输入的新密码不一致');
      return;
    }

    setSubmitting(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (!result.success) {
        setIsError(true);
        setMessage(result.message || '密码修改失败');
        return;
      }

      setIsError(false);
      setMessage(result.message || '密码修改成功');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : '密码修改失败');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordInput = (
    field: PasswordField,
    label: string,
    value: string,
    onChange: (value: string) => void,
    autoComplete: string,
  ) => (
    <label className="block space-y-2 text-sm font-bold text-gray-700">
      <span>{label}</span>
      <div className="relative">
        <input
          aria-label={label}
          autoComplete={autoComplete}
          className={inputClass}
          type={visible[field] ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          aria-label={`${visible[field] ? '隐藏' : '显示'}${label}`}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-400 transition hover:bg-white hover:text-gray-700"
          type="button"
          onClick={() => toggleVisibility(field)}
        >
          {visible[field] ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
    </label>
  );

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-[#FFE815] p-3 text-black shadow-sm shadow-yellow-200">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900">账户安全</h3>
            <p className="mt-1 text-sm text-gray-500">
              修改当前后台登录密码，保存后请使用新密码登录。
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start rounded-full bg-green-50 px-3 py-1.5 text-xs font-bold text-green-700 sm:self-auto">
          <KeyRound className="h-3.5 w-3.5" />
          密码受保护
        </div>
      </div>

      <form
        aria-label="修改登录密码"
        className="space-y-5 p-6"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {passwordInput(
            'current',
            '当前密码',
            currentPassword,
            setCurrentPassword,
            'current-password',
          )}
          {passwordInput(
            'next',
            '新密码',
            newPassword,
            setNewPassword,
            'new-password',
          )}
          {passwordInput(
            'confirm',
            '确认新密码',
            confirmPassword,
            setConfirmPassword,
            'new-password',
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-h-10 flex-1">
            {message && (
              <div
                aria-live="polite"
                className={`rounded-xl px-4 py-2.5 text-sm font-bold ${
                  isError
                    ? 'bg-red-50 text-red-600'
                    : 'bg-green-50 text-green-700'
                }`}
              >
                {message}
              </div>
            )}
          </div>
          <button
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-bold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
            type="submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {submitting ? '修改中…' : '修改登录密码'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default AccountSecurityCard;
